import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useHistory } from "react-router-dom";
import { HiOutlineTrash, HiOutlinePlus } from "react-icons/hi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import API from "../../api";
import { capture } from "../../instrument";
import { INPUT_CLASS_NAME, TUBE_TYPE, TUBE_TYPE_LABELS, TUBE_TYPE_COLORS } from "../../utils";
import CodeLookup from "../../components/CodeLookup";

const AddCode = () => {
  const history = useHistory();
  const queryClient = useQueryClient();

  const [value, setValue] = useState("");
  const [name, setName] = useState("");
  const [codeType, setCodeType] = useState("");
  const [type, setType] = useState("DEFAULT");
  const [sampleId, setSampleId] = useState("");

  const [UMLS, setUMLS] = useState("");
  const [ubilabUrl, setUbilabUrl] = useState("");
  const [relatedCodes, setRelatedCodes] = useState([]);
  const [incompatibleCodes, setIncompatibleCodes] = useState([]);
  const [enabledAtFrontDesk, setEnabledAtFrontDesk] = useState(true);
  const [tubes, setTubes] = useState([]);

  const createMutation = useMutation({
    mutationFn: (body) => API.post("/code", body),
    onSuccess: (response) => {
      if (!response.ok) {
        if (response.code === "CODE_ALREADY_EXISTS") return toast.error("Un code avec cette valeur existe déjà");
        return toast.error(response.message);
      }
      toast.success("Code créé avec succès");
      queryClient.invalidateQueries({ queryKey: ["codes"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
      history.push("/code");
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur lors de la création du code");
    },
  });

  const createCode = () => {
    if (!value) return toast.error("La valeur du code est requise");
    createMutation.mutate({
      value,
      name,
      codeType: codeType || null,
      type,
      sampleId,
      UMLS,
      ubilabUrl,
      relatedCodes: relatedCodes.map((c) => c._id).filter(Boolean),
      incompatibleCodes: incompatibleCodes.map((c) => c._id).filter(Boolean),
      enabledAtFrontDesk,
      tubes,
    });
  };

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-gray-900">Ajouter un code</h1>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <button
            type="button"
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
            onClick={() => history.push("/code")}
          >
            Annuler
          </button>
          <button
            type="button"
            className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            onClick={createCode}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Chargement..." : "Ajouter"}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-4xl px-8 py-6">
            <h3 className="mb-4 text-lg font-medium text-gray-900">Général</h3>

            <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Nom</label>
                <input className={INPUT_CLASS_NAME} value={name} onChange={(e) => setName(e.target.value)} placeholder="Numération Formule Sanguine" />
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Valeur</label>
                <input className={INPUT_CLASS_NAME} placeholder="NFS" value={value} onChange={(e) => setValue(e.target.value)} />
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Type de code</label>
                <select className={INPUT_CLASS_NAME} value={codeType} onChange={(e) => setCodeType(e.target.value)}>
                  <option value="">—</option>
                  <option value="groupe">Groupe</option>
                  <option value="analyse">Analyse</option>
                </select>
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Type</label>
                <select className={INPUT_CLASS_NAME} value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="DEFAULT">DEFAULT</option>
                  <option value="SANG">SANG</option>
                  <option value="ECHANTILLON">ECHANTILLON</option>
                  <option value="H24">H24</option>
                  <option value="SEROLOGIE">SEROLOGIE</option>
                  <option value="PCR">PCR</option>
                  <option value="SELLE">SELLE</option>
                  <option value="AUTRE">AUTRE</option>
                </select>
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Code échantillon</label>
                <input className={INPUT_CLASS_NAME} placeholder="Code échantillon" value={sampleId} onChange={(e) => setSampleId(e.target.value)} />
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">UMLS</label>
                <input className={INPUT_CLASS_NAME} placeholder="UMLS" value={UMLS} onChange={(e) => setUMLS(e.target.value)} />
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">URL Ubilab</label>
                <input className={INPUT_CLASS_NAME} placeholder="https://ubilab.io/..." value={ubilabUrl} onChange={(e) => setUbilabUrl(e.target.value)} />
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Sous-codes associés</label>
                <div className="flex-1">
                  <CodeLookup initialValues={relatedCodes} onChange={setRelatedCodes} placeholder="Rechercher un code..." allowManualEntry={false} />
                </div>
              </div>
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Codes incompatibles</label>
                <div className="flex-1">
                  <CodeLookup initialValues={incompatibleCodes} onChange={setIncompatibleCodes} placeholder="Rechercher un code..." allowManualEntry={false} />
                </div>
              </div>
              <div className="flex items-start gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 pt-1.5 text-[13px] font-medium text-gray-600">Tubes à prélever</label>
                <div className="flex flex-1 flex-col gap-2">
                  {tubes.map((tube, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        className={`${INPUT_CLASS_NAME} !w-16 !flex-none text-center`}
                        value={tube.quantity}
                        onChange={(e) => {
                          const updated = [...tubes];
                          updated[index] = { ...updated[index], quantity: Math.max(1, parseInt(e.target.value) || 1) };
                          setTubes(updated);
                        }}
                      />
                      <div className="relative flex flex-1 items-center">
                        <span className={`pointer-events-none absolute left-2.5 h-2 w-2 rounded-full ${TUBE_TYPE_COLORS[tube.type]?.dot || "bg-gray-400"}`} />
                        <select
                          className={`${INPUT_CLASS_NAME} pl-7`}
                          value={tube.type}
                          onChange={(e) => {
                            const updated = [...tubes];
                            updated[index] = { ...updated[index], type: e.target.value };
                            setTubes(updated);
                          }}
                        >
                          {Object.values(TUBE_TYPE).map((tubeType) => (
                            <option key={tubeType} value={tubeType}>
                              {TUBE_TYPE_LABELS[tubeType]}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                        onClick={() => setTubes(tubes.filter((_, i) => i !== index))}
                      >
                        <HiOutlineTrash className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm text-gray-500 transition-colors hover:text-primary-500"
                    onClick={() => setTubes([...tubes, { quantity: 1, type: TUBE_TYPE.EDTA }])}
                  >
                    <HiOutlinePlus className="h-4 w-4" />
                    Ajouter un tube
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between gap-4 px-5 py-3.5">
                <div className="w-44 shrink-0">
                  <span className="text-[13px] font-medium text-gray-600">Disponible à la saisie</span>
                  <span className="block text-xs text-gray-500">Autocomplete de l'accueil</span>
                </div>
                <button
                  type="button"
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${enabledAtFrontDesk ? "bg-primary-500" : "bg-gray-300"}`}
                  onClick={() => setEnabledAtFrontDesk(!enabledAtFrontDesk)}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enabledAtFrontDesk ? "translate-x-5" : "translate-x-0"}`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCode;
